<template>
    <div class="template-setup">
        <div class="section-card">
            <h3>{{ $t('whatsapp_business_api.setupYourTemplate') }}</h3>
            <p class="description">
                {{ $t('whatsapp_business_api.setupDescription') }}
                <a
                    href="https://developers.facebook.com/docs/whatsapp/message-templates/guidelines"
                    target="_blank"
                >{{ $t('whatsapp_business_api.learnMoreCategories') }}</a>
            </p>

            <!-- Category Tabs -->
            <div class="category-tabs">
                <div
                    class="category-tab"
                    :class="{ 
                        active: form.category === 'MARKETING',
                        disabled: isCategoryDisabled
                    }"
                    @click="!isCategoryDisabled && selectCategory('MARKETING')"
                >
                    <span class="material-icons">campaign</span>
                    <span>{{ $t('whatsapp_business_api.marketing') }}</span>
                    <a-tooltip v-if="isCategoryDisabled" :title="$t('whatsapp_business_api.categoryCannotChangeApproved')">
                        <span class="material-icons" style="font-size: 16px; margin-left: 4px;">info</span>
                    </a-tooltip>
                </div>
                <div
                    class="category-tab"
                    :class="{ 
                        active: form.category === 'UTILITY',
                        disabled: isCategoryDisabled
                    }"
                    @click="!isCategoryDisabled && selectCategory('UTILITY')"
                >
                    <span class="material-icons">build</span>
                    <span>{{ $t('whatsapp_business_api.utility') }}</span>
                    <a-tooltip v-if="isCategoryDisabled" :title="$t('whatsapp_business_api.categoryCannotChangeApproved')">
                        <span class="material-icons" style="font-size: 16px; margin-left: 4px;">info</span>
                    </a-tooltip>
                </div>
                <div
                    class="category-tab"
                    :class="{ 
                        active: form.category === 'AUTHENTICATION',
                        disabled: isCategoryDisabled
                    }"
                    @click="!isCategoryDisabled && selectCategory('AUTHENTICATION')"
                >
                    <span class="material-icons">verified_user</span>
                    <span>{{ $t('whatsapp_business_api.authentication') }}</span>
                    <a-tooltip v-if="isCategoryDisabled" :title="$t('whatsapp_business_api.categoryCannotChangeApproved')">
                        <span class="material-icons" style="font-size: 16px; margin-left: 4px;">info</span>
                    </a-tooltip>
                </div>
            </div>
            <div v-if="isCategoryDisabled" class="edit-mode-notice" style="margin-top: 12px; padding: 12px; background: #fff7e6; border: 1px solid #ffd591; border-radius: 4px; color: #ad6800; font-size: 13px;">
                <span class="material-icons" style="font-size: 16px; vertical-align: middle; margin-right: 4px;">info</span>
                <span>{{ $t('whatsapp_business_api.categoryCannotEditApproved') }}</span>
            </div>

            <!-- Template Type Options -->
            <div class="template-types">
                <a-tooltip
                    v-for="type of templateTypes"
                    :key="type.id"
                    :title="type.disabled ? type.disabledReason : ''"
                    :mouseEnterDelay="0.5"
                >
                    <div
                        class="template-type-option"
                        :class="{ 
                            selected: form.templateType === type.id,
                            disabled: type.disabled 
                        }"
                        @click="!type.disabled && selectTemplateType(type.id)"
                    >
                        <a-radio :checked="form.templateType === type.id" :disabled="type.disabled" />
                        <div class="type-info">
                            <span class="type-label">
                                {{ type.label }}
                                <a-tag v-if="type.disabled" color="orange" style="margin-left: 8px; font-size: 11px;">{{ $t('whatsapp_business_api.notSupported') }}</a-tag>
                            </span>
                            <span class="type-desc">{{ type.description }}</span>
                        </div>
                    </div>
                </a-tooltip>
            </div>

            <!-- Catalogue Sub-Type Selection -->
            <div v-if="form.templateType === 'CATALOGUE'" class="catalogue-subtypes" style="margin-top: 24px;">
                <h4 style="margin-bottom: 12px; font-size: 14px; color: #666;">{{ $t('whatsapp_business_api.selectProductMessageType') }}</h4>
                <div class="template-types">
                    <a-tooltip
                        v-for="subType of catalogueSubtypes"
                        :key="subType.id"
                        :title="subType.disabled ? subType.disabledReason : ''"
                        :mouseEnterDelay="0.5"
                    >
                        <div
                            class="template-type-option"
                            :class="{ 
                                selected: form.catalogueSubType === subType.id,
                                disabled: subType.disabled 
                            }"
                            @click="!subType.disabled && selectCatalogueSubType(subType.id)"
                        >
                            <a-radio :checked="form.catalogueSubType === subType.id" :disabled="subType.disabled" />
                            <div class="type-info">
                                <span class="type-label">
                                    {{ subType.label }}
                                    <a-tag v-if="subType.disabled" color="orange" style="margin-left: 8px; font-size: 11px;">{{ $t('whatsapp_business_api.notSupported') }}</a-tag>
                                </span>
                                <span class="type-desc">{{ subType.description }}</span>
                            </div>
                        </div>
                    </a-tooltip>
                </div>
            </div>

            <!-- Authentication Sub-Type Selection -->
            <div v-if="form.category === 'AUTHENTICATION'" class="authentication-subtypes" style="margin-top: 24px;">
                <h4 style="margin-bottom: 12px; font-size: 14px; color: #666;">{{ $t('whatsapp_business_api.selectOtpAutofillType') }}</h4>
                <div class="template-types">
                    <div
                        v-for="subType of authenticationSubtypes"
                        :key="subType.id"
                        class="template-type-option"
                        :class="{ selected: form.authentication.otpSubType === subType.id }"
                        @click="selectAuthSubType(subType.id)"
                    >
                        <a-radio :checked="form.authentication.otpSubType === subType.id" />
                        <div class="type-info">
                            <span class="type-label">{{ subType.label }}</span>
                            <span class="type-desc">{{ subType.description }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "TemplateSetup",
    props: {
        form: { type: Object, required: true },
        templateTypes: { type: Array, required: true },
        catalogueSubtypes: { type: Array, default: () => [] },
        authenticationSubtypes: { type: Array, default: () => [] },
        editMode: { type: Boolean, default: false },
        templateStatus: { type: String, default: null },
    },
    computed: {
        // Meta API kuralı: APPROVED template'lerin category'si edit edilemez
        isCategoryDisabled() {
            return this.editMode && this.templateStatus === "APPROVED";
        },
    },
    methods: {
        selectCategory(category) {
            this.$emit('update-category', category);
        },
        selectTemplateType(typeId) {
            this.$emit('update-template-type', typeId);
        },
        selectCatalogueSubType(subType) {
            this.$emit('update-catalogue-subtype', subType);
        },
        selectAuthSubType(subType) {
            this.$emit('update-auth-subtype', subType);
        },
    },
});
</script>

<style scoped lang="scss">
.template-setup {
    .section-card {
        background: white;
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

        h3 {
            margin: 0 0 8px 0;
            font-weight: 600;
        }

        .description {
            color: #666;
            font-size: 14px;
            margin-bottom: 20px;

            a {
                color: #1890ff;
            }
        }
    }

    .category-tabs {
        display: flex;
        gap: 0;
        margin-bottom: 24px;
        border: 1px solid #d9d9d9;
        border-radius: 8px;
        overflow: hidden;

        .category-tab {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 16px;
            cursor: pointer;
            background: white;
            border-right: 1px solid #d9d9d9;
            transition: all 0.2s;

            &:last-child {
                border-right: none;
            }

            &:hover {
                background: #f5f5f5;
            }

            &.active {
                background: #1890ff;
                color: white;
            }
            
            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
                background: #fafafa;
                
                &:hover {
                    background: #fafafa;
                }
            }

            .material-icons {
                font-size: 20px;
            }
        }
    }

    .template-types {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .template-type-option {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px;
            border: 1px solid #d9d9d9;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s;

            &:hover:not(.disabled) {
                border-color: #1890ff;
                background: #f0f7ff;
            }

            &.selected {
                border-color: #1890ff;
                background: #e6f7ff;
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
                background: #fafafa;
                
                &:hover {
                    border-color: #d9d9d9;
                }
            }

            .type-info {
                display: flex;
                flex-direction: column;

                .type-label {
                    font-weight: 600;
                    margin-bottom: 4px;
                    display: flex;
                    align-items: center;
                }

                .type-desc {
                    font-size: 13px;
                    color: #666;
                }
            }
        }
    }
}
</style>
